import type { $Fetch } from 'ofetch';
import type { CatalogReadGateway } from '@/application/port/out/CatalogReadGateway';
import type {
  CourseModality,
  CourseStatus,
  CourseSummary,
  CurriculumStatus,
  CurriculumSummary,
} from '@/domain/model/Catalog';
import type { ApiResponse, SpringPage } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

interface CourseDto {
  id: string;
  name: string;
  code: string;
  modality: CourseModality;
  status: CourseStatus;
}

interface CurriculumDto {
  id: string;
  educationLevelId: string | null;
  version: string;
  label: string;
  status: CurriculumStatus;
}

export class HttpCatalogReadGateway implements CatalogReadGateway {
  constructor(private readonly client: $Fetch) {}

  async listCourses(): Promise<CourseSummary[]> {
    const courses: CourseSummary[] = [];
    let page = 0;
    let totalPages = 1;
    do {
      const res = await this.client<ApiResponse<SpringPage<CourseDto>>>('/api/v1/courses', { method: 'GET', params: { page, size: 100, sort: 'name,asc' } });
      assertApiSuccess(res);
      courses.push(...res.data.content.map(c => ({ id: c.id, name: c.name, code: c.code, modality: c.modality, status: c.status })));
      totalPages = res.data.totalPages;
      page++;
    } while (page < totalPages);
    return courses;
  }

  async listCurriculumsByCourse(courseId: string): Promise<CurriculumSummary[]> {
    const res = await this.client<ApiResponse<CurriculumDto[]>>(
      `/api/v1/curriculums/courses/${encodeURIComponent(courseId)}`,
      { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data.map((c) => ({
      id: c.id,
      educationLevelId: c.educationLevelId,
      version: c.version,
      label: c.label,
      status: c.status,
    }));
  }
}
